import { z } from 'zod';
import { uuidSchema } from './common.js';
export const KIOSK_DEVICE_STATUSES = ['unpaired', 'active', 'disabled'];
export const createKioskDeviceSchema = z.object({
    name: z.string().trim().min(1).max(120),
    locationId: uuidSchema,
    note: z.string().trim().max(500).nullable().default(null),
});
/** t1168: what a checkout tablet reports about its own lockdown and health.
 *  Every field is optional because two sources fill it in: the socket
 *  heartbeat (the full picture) and the plain HTTP heartbeat (only lock-task
 *  state and battery), merged on the server. The Pi never sends it. */
export const KIOSK_LOCK_STATES = ['locked', 'unlocked', 'setup_needed'];
export const kioskTabletStatusSchema = z.object({
    /** `locked`: the tablet is held in the kiosk app. `unlocked`: staff (or the
     *  back office) released it for servicing. `setup_needed`: it can't lock
     *  itself until the one-time lockdown permission is granted on the tablet. */
    lock: z.enum(KIOSK_LOCK_STATES).optional(),
    lockTaskActive: z.boolean().optional(),
    watchdogEnabled: z.boolean().optional(),
    deviceOwner: z.boolean().optional(),
    /** Whether lockdown re-arms itself after an app update. */
    updateProof: z.boolean().optional(),
    batteryPct: z.number().int().min(0).max(100).optional(),
    charging: z.boolean().optional(),
});
export const kioskDeviceSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    locationId: uuidSchema,
    locationName: z.string().nullable(),
    status: z.enum(KIOSK_DEVICE_STATUSES),
    /** True if the device has checked in recently (heartbeat, HTTP or socket).
     *  Does NOT mean a command can be delivered right now — see `connected`. */
    online: z.boolean(),
    /** True only if the device is holding a live command socket this instant.
     *  A device can be `online` (reporting in) but not `connected` (no socket
     *  to push a command down) — e.g. a tablet on a build older than t0603,
     *  which only heartbeats over plain HTTP, or one mid-reconnect. */
    connected: z.boolean(),
    lastSeenAt: z.iso.datetime().nullable(),
    agentVersion: z.string().nullable(),
    /** Android versionCode from the tablet heartbeat; null for the Pi. */
    appVersionCode: z.number().int().nullable(),
    /** t0660: set together, cleared together once the tablet reports it has
     *  reached (or passed) the target build. Null/null = no update pending. */
    updateRequestedAt: z.iso.datetime().nullable(),
    updateTargetVersionCode: z.number().int().nullable(),
    /** Whether this tablet is device-owner provisioned, as last reported on
     *  its heartbeat — only a device-owner tablet can install an update
     *  silently. Null for the Pi and for a tablet build too old to report it. */
    deviceOwner: z.boolean().nullable(),
    /** t1168: the tablet's last-reported lock and health state; null for the
     *  Pi and for a tablet that has not reported one yet. */
    tabletStatus: kioskTabletStatusSchema.extend({ reportedAt: z.iso.datetime() }).nullable(),
    lastError: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
/** Body of `POST /kiosks/:id/request-update` (t0660) — the versionCode is
 *  supplied by the caller (the admin SPA's already-fetched release manifest)
 *  rather than looked up server-side, so the admin always requests exactly
 *  the build it showed the operator. */
export const kioskRequestUpdateSchema = z.object({
    versionCode: z.number().int().positive(),
});
/** Returned once, at pairing-code creation. Never retrievable again. */
export const kioskPairingCodeSchema = z.object({
    pairingCode: z.string(),
    expiresAt: z.iso.datetime(),
});
export const kioskPairRequestSchema = z.object({
    pairingCode: z.string().trim().toUpperCase().length(8),
    agentVersion: z.string().trim().max(40).default('0.0.0'),
});
/** The token is shown exactly once — the server only ever stores its hash. */
export const kioskPairResultSchema = z.object({
    deviceId: uuidSchema,
    deviceName: z.string(),
    token: z.string(),
    expiresAt: z.iso.datetime(),
});
/** Everything a `dispense` command can report back. */
export const VEND_COMMAND_OUTCOMES = ['dispensed', 'jammed', 'empty', 'error'];
/** Messages the agent sends up the outbound WebSocket. */
export const kioskAgentMessageSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('heartbeat'),
        agentVersion: z.string().max(40),
        uptimeSeconds: z.number().int().nonnegative(),
        screenOn: z.boolean().optional(),
        temperatureC: z.number().optional(),
        /** Pi-reported machine health — absent until a real driver replaces
         *  `SimulatedHardware`'s `machineStatus()`. Feeds the fleet dashboard's
         *  third health dot; it is not itself part of the vend protocol. */
        machine: z.object({ online: z.boolean(), detail: z.string().max(500).optional() }).optional(),
        /** t1168: the checkout tablet's lockdown and battery state. */
        tablet: kioskTabletStatusSchema.optional(),
    }),
    z.object({ type: z.literal('error'), message: z.string().max(1000) }),
    z.object({
        type: z.literal('command_result'),
        commandId: z.string().max(64),
        ok: z.boolean(),
        detail: z.string().max(1000).nullable().default(null),
        /** Present only for the result of a `dispense` command — the id of the
         *  `vend_attempts` row this command authorized, echoed back so the
         *  server can correlate the result without trusting `commandId` alone. */
        vendId: z.string().max(64).optional(),
        outcome: z.enum(VEND_COMMAND_OUTCOMES).optional(),
    }),
]);
/**
 * Commands the server pushes down. `dispense` is deliberately not exposed on
 * the generic admin `POST /kiosks/:id/command` route — only `VendService` may
 * ever construct one, because a command on this channel is what makes a
 * machine move. `capture_media` is kept off it for a different reason: its
 * params carry the camera's URL, which may hold a login, so only
 * `CameraMediaService` builds one.
 */
export const kioskServerMessageSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('heartbeat_ack'), serverTime: z.iso.datetime() }),
    z.object({
        type: z.literal('command'),
        commandId: z.string().max(64),
        action: z.enum([
            'reboot',
            'reload_ui',
            'screen_on',
            'screen_off',
            'capture_media',
            'dispense',
            // t1168: tablet only. `check_in` makes it send a heartbeat right now.
            'kiosk_lock',
            'kiosk_unlock',
            'check_in',
        ]),
        params: z.record(z.string(), z.unknown()).default({}),
    }),
    z.object({ type: z.literal('rotate_token'), token: z.string(), expiresAt: z.iso.datetime() }),
]);
/** The shape `params` takes on specifically for a `dispense` command — not
 *  enforced by `kioskServerMessageSchema` itself (params stays a generic
 *  record there, same as every other action), but this is what `VendService`
 *  writes and what the kiosk-agent's simulator reads back out. */
export const dispenseCommandParamsSchema = z.object({
    vendId: z.string().max(64),
    slot: z.string().max(20),
});
//# sourceMappingURL=kiosk.js.map